import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronLeft, Store } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import ShopGovernanceModal, { type ShopConfirmModalState } from '../components/shops/ShopGovernanceModal';
import ManagedShopWalletHistoryPanel from '../components/shops/ManagedShopWalletHistoryPanel';
import { ShopStatusBadge } from '../components/shops/shopStatusBadge';
import { useAuth } from '../hooks/useAuth';
import { fetchShop, suspendShop, unsuspendShop } from '../services/api';
import type { AgentShop } from '../types';
import { formatCostRate } from '../utils/costFormat';

export default function ShopDetailPage() {
  const { user } = useAuth();
  const { shopId } = useParams<{ shopId: string }>();
  const [shop, setShop] = useState<AgentShop | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<ShopConfirmModalState | null>(null);

  const minShopCostExclusive = useMemo(() => {
    const fromShop = shop?.minShopCostRateExclusive;
    if (fromShop != null && Number.isFinite(Number(fromShop))) {
      const n = Number(fromShop);
      return Math.min(1, Math.max(0, n));
    }
    const u = user?.costRate;
    if (u != null && Number.isFinite(u)) return Math.min(1, Math.max(0, u));
    return 0;
  }, [shop?.minShopCostRateExclusive, user?.costRate]);

  const loadShop = useCallback(async () => {
    if (!shopId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const shopRes = await fetchShop(shopId);
      if (shopRes.success && shopRes.data) {
        setShop(shopRes.data);
      } else {
        setShop(null);
        setNotFound(true);
        if (shopRes.message) toast.error(shopRes.message);
      }
    } catch {
      setShop(null);
      setNotFound(true);
      toast.error('Could not load shop');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    void loadShop();
  }, [loadShop]);

  const runShopAction = async (
    fn: () => Promise<{ success: boolean; message?: string }>,
    successMessage?: string,
  ): Promise<boolean> => {
    if (!shop) return false;
    setRowBusy(shop.id);
    try {
      const res = await fn();
      if (!res.success) {
        toast.error(res.message || 'Action failed');
        return false;
      }
      if (successMessage) toast.success(successMessage);
      await loadShop();
      return true;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Action failed';
      toast.error(msg);
      return false;
    } finally {
      setRowBusy(null);
    }
  };

  if (!shopId) {
    return (
      <AppLayout>
        <p className="text-gray-600">Invalid shop.</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Link
        to="/shops"
        className="mb-4 inline-flex items-center gap-0.5 text-sm font-semibold text-orange-700 hover:text-orange-900"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        Back to shops
      </Link>

      {loading ? (
        <PageLoadingIndicator variant="page" />
      ) : notFound || !shop ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <Store className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-gray-600">Shop not found</p>
          <Link to="/shops" className="mt-4 inline-block text-sm font-semibold text-orange-700 hover:text-orange-900">
            Return to shop list
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{shop.name}</h1>
            </div>
            <div className="flex flex-wrap gap-2">
              {shop.status === 'active' && (
                <button
                  type="button"
                  disabled={!!rowBusy}
                  onClick={() => setConfirmModal({ type: 'suspend', shop })}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                >
                  Suspend
                </button>
              )}
              {shop.status === 'suspended' && (
                <button
                  type="button"
                  disabled={!!rowBusy}
                  onClick={() =>
                    void runShopAction(() => unsuspendShop(shop.id), 'Shop reactivated')
                  }
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                >
                  Unsuspend
                </button>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-orange-100 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Info</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{shop.email}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd className="font-medium text-gray-900">{shop.phone || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Username</dt>
                <dd className="font-medium text-gray-900">
                  {shop.username?.trim() ? shop.username : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="mt-1">
                  <ShopStatusBadge status={shop.status} />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Cost rate</dt>
                <dd className="font-medium text-gray-900">{formatCostRate(shop.costRate)}</dd>
                <dd className="mt-1 text-xs text-gray-500">
                  Parent agent cost floor: {formatCostRate(minShopCostExclusive)}.
                </dd>
              </div>
            </dl>
          </div>

          <ManagedShopWalletHistoryPanel shopId={shop.id} />
        </>
      )}

      <ShopGovernanceModal
        state={confirmModal}
        onClose={() => setConfirmModal(null)}
        busyShopId={rowBusy}
        onConfirmSuspend={(id) => runShopAction(() => suspendShop(id), 'Shop suspended')}
      />
    </AppLayout>
  );
}
